import { TProduct as BaseProduct } from 'types/models/product';
import { debounce } from 'widgets/toolbox/debounce';
import { pageProperties, report } from '../utils/base';

type TProduct = BaseProduct & { index: number; item_list_name: string };

export type TImpression = {
    item_id: string;
    quantity: number;
    index?: number;
    item_name: string;
    item_list_name?: string;
    price?: string;
    item_category?: string;
    item_brand?: string;
    coupon?: Array<string>;
    item_variant?: string;
};

declare global {
    interface Window {
        gtmImpressions?: {
            ecommerce?: {
                currencyCode: string;
                impressions?: Array<TImpression>;
            };
        };
        productPositions?: {
            [key: string]: number;
        };
    }
}

/**
 * @description GTM impressions
 * @module impressions
 * @category widgets
 * @subcategory int_gtm
 */

export let impressionsDebounce: Array<TImpression> = [];

const impressedProducts: Array<string> = [];

/**
 * @description Added product to impressed product list
 * @param product - product with id, position and variant
 */
export function addToImpressedProducts(product: TImpression) {
    const impressionUniqueId = `${product.item_id}|${product.index || ''}|${product.item_variant || ''}`;

    impressedProducts.push(impressionUniqueId);
}

/**
 * @description Checks if product is already impressed
 * @param id -product ID
 * @param position  - product position
 * @param variant - product variant
 * @returns result
 */
export function hasBeenImpressed(id: string, position?: string | number, variant?: string): boolean {
    const impressionUniqueId = `${id}|${position || ''}|${variant || ''}`;

    return impressedProducts.includes(impressionUniqueId);
}

/**
 * @description Returns wishlist products impression data
 * @param products - products data
 * @returns result
 */
export function getWishlistImpressions(products: Array<TProduct>) {
    const impressions: Array<TImpression> = [];

    if (!products || !products.length) {
        return impressions;
    }

    for (let index = 0; index < products.length; index++) {
        const product = products[index];

        if (product && product.gtmInfo && !hasBeenImpressed(product.gtmInfo?.item_id, index + 1, product.gtmInfo?.item_variant)) {
            impressions.push({
                item_list_name: 'Wishlist',
                index: index + 1,
                ...product.gtmInfo
            });
        }
    }

    return impressions;
}

/**
 * @description Reports GTM promo impressions on page load
 */
export function reportPromoImpressions(): void {
    let promoImpressions: Array<HTMLElement> = Array.from(document.querySelectorAll('.js-promo-impression'));

    promoImpressions = promoImpressions.map((impressionEl) => {
        try {
            const promoDetails = JSON.parse(<string>impressionEl.dataset.promoDetails);

            /**
             * @description Tracks to dataLayer an information about click on promotion details
             * @listens dom:click
             */
            impressionEl.addEventListener('click', () => {
                report('event', 'view_promotion', {
                    promotions: [promoDetails]
                });
            }, { passive: true, once: true });

            return promoDetails;
        } catch (e) {
            setTimeout(() => {
                throw e;
            }, 0);

            return false;
        }
    }).filter(Boolean);

    if (promoImpressions.length) {
        report('event', 'pageLoad', {
            promotions: promoImpressions
        });
    }
}

/**
 * @description Reports GTM product impressions on page load
 */
export function reportProductImpressions(): void {
    if (window.gtmImpressions?.ecommerce?.impressions?.length) {
        window.gtmImpressions.ecommerce.impressions.forEach(impression => {
            const position = <number> impression.index || window.productPositions?.[impression.item_id];

            if (!hasBeenImpressed(impression.item_id, position, impression.item_variant)) {
                if (position) {
                    impression.index = position;

                    if (!impression.item_list_name && window.location.search.indexOf('q=') > -1) {
                        impression.item_list_name = pageProperties.searchList;
                    }
                }

                addToImpressedProducts(impression);
            }
        });

        report('event', 'view_item_list', {
            currency: window.gtmImpressions.ecommerce.currencyCode,
            items: window.gtmImpressions.ecommerce.impressions
        });
    }
}

/**
 * @description Sends products impressions to dataLayer
 */
export const reportImpression = debounce(() => {
    const event = {
        currency: pageProperties.currentCurrency,
        items: impressionsDebounce
    };

    report('event', 'view_item_list', event);
    impressionsDebounce = [];
}, 50);
